import {Component, OnInit, Optional, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import {Advertiser} from '../../../../../models/advertiser';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {DialogService} from '../../../../../services/dialog/dialog.service';
import {AdvertiserService} from '../../../../../services/advertiser/advertiser.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {CountryService} from '../../../../../services/country/country.service';
import {Country} from '../../../../../models/country';
import {IsGranted} from '../../../../../decorator/is-granted';
import {AuthService} from '../../../../../services/auth/auth.service';
import {UploadFile} from '../../../../../models/upload-file';
import {UploadFileService} from '../../../../../services/upload-file/upload-file.service';
import {ImageService} from '../../../../../services/image/image.service';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {ComplianceDocumentsComponent} from '../../../../layout/compliance-documents/compliance-documents.component';
import {PartnerTabComponent} from '../../../partners/partner-tab/partner-tab.component';

@Component({
  selector: 'app-advertiser-form',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    FormsModule,
    NgOptimizedImage,
    ComplianceDocumentsComponent
],
  templateUrl: './advertiser-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './advertiser-form.component.scss'
})
@IsGranted(['ROLE_API_ADVERTISER_WRITE', 'ADVERTISER_VIEW'])
@MetaTitle('Advertiser form')
export class AdvertiserFormComponent implements OnInit {

  @ViewChild('compliance') complianceComponent!: ComplianceDocumentsComponent;

  advertiser: Advertiser | null = null;
  countries: Country[] = [];

  logo: UploadFile | null = null;

  form = new FormGroup({
    companyName: new FormControl('', Validators.required),
    firstname: new FormControl(''),
    lastname: new FormControl(''),
    nickname: new FormControl(''),
    address: new FormControl(''),
    zipCode: new FormControl(''),
    state: new FormControl(''),
    city: new FormControl(''),
    country: new FormControl(''),
    phoneNumber: new FormControl(''),
    email: new FormControl(''),
    websiteUrl: new FormControl(''),
    companyNumber: new FormControl(''),
    vatNumber: new FormControl(''),
    status: new FormControl(''),
  });

  constructor(
    protected imageService: ImageService,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private dialog: DialogService,
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private advertiserService: AdvertiserService,
    private countryService: CountryService,
    private uploadFileService: UploadFileService
  ) {
  }

  ngOnInit(): void {
    this.loader.startLoading();

    this.countryService.findAll().subscribe(countries => {
      this.countries = countries;

      const id = Number(this.route.snapshot.paramMap.get('id'));

      if (id) {
        this.advertiserService.findOneById(id, {groups: 'documents'}).subscribe((advertiser: Advertiser) => {
          this.advertiser = advertiser;
          this.form.patchValue({
            companyName: this.advertiser.companyName,
            firstname: this.advertiser.firstname,
            lastname: this.advertiser.lastname,
            nickname: this.advertiser.nickname,
            address: this.advertiser.address,
            zipCode: this.advertiser.zipCode,
            state: this.advertiser.state,
            city: this.advertiser.city,
            country: this.advertiser.country,
            phoneNumber: this.advertiser.phoneNumber,
            email: this.advertiser.email,
            websiteUrl: this.advertiser.websiteUrl,
            companyNumber: this.advertiser.companyNumber,
            vatNumber: this.advertiser.vatNumber,
            status: this.advertiser.status,
          });

          this.loader.stopLoading();
        });
      } else {
        this.loader.stopLoading();
      }
    });
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    this.loader.startLoading();

    let params: any = this.form.value;

    if (this.isCreate()) {
      this.advertiserService.create(params).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Advertiser has been created.');
        this.router.navigateByUrl('/configuration/advertisers').then();
      });
    } else if (this.advertiser?.id) {
      if (this.logo) {
        params.logo = this.logo;
      }

      this.complianceComponent?.sendUploadedFile();

      this.advertiserService.update(this.advertiser.id, params).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Advertiser has been modified.');
        this.router.navigateByUrl('/configuration/advertisers').then();
      });
    }
  }

  isCreate(): boolean {
    return !this.advertiser;
  }

  canUpdate(): boolean {
    return this.auth.hasRolesSync(['ROLE_API_ADVERTISER_WRITE']);
  }

  onClickLogoUpload(event: Event): void {
    const target: any = event.target;

    this.uploadLogo(target?.files[0]);
  }

  onDropUpload(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;

    if (files && files.length > 0) {
      this.uploadLogo(files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  private uploadLogo(file: File): void {
    this.uploadFileService.upload(file).subscribe((uploadFile: UploadFile) => {
      this.logo = uploadFile;
    });
  }
}
